import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Building2, Clock, ChevronRight } from 'lucide-react';

const API_BASE = '';
const FLAG_MAP = { 'USA': 'https://media.api-sports.io/flags/us.svg', 'Canada': 'https://media.api-sports.io/flags/ca.svg', 'Mexico': 'https://media.api-sports.io/flags/mx.svg' };

export default function WorldCupCities() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/venues`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  return (
    <div data-testid="wc-cities-page">
      <WCSubmenu />
      <SEOHead title={t('wc.cities') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'} />

      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1a2744 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-8 h-8 inline mr-3 text-amber-400" />{t('wc.hostCitiesTitle')}
          </h1>
          <p className="text-sm text-slate-400">{data.cities?.length} {t('wc.cities').toLowerCase()}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.cities?.map(city => (
            <Link key={city.slug || city.name} to={`${wcBase}/${currentLang === "tr" ? "ev-sahibi" : "host"}/${currentLang === "tr" ? (city.country_slug === "usa" ? "abd" : city.country_slug === "canada" ? "kanada" : city.country_slug === "mexico" ? "meksika" : city.country_slug) : city.country_slug}/${city.slug}`}
              data-testid={`city-link-${city.slug}`}
              className="group bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(245,158,11,0.1)]">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_MAP[city.country]} alt="" className="w-6 h-4 rounded-sm shadow" />
                    <h3 className="text-base font-bold text-white group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{city.name}</h3>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 transition-colors shrink-0" />
                </div>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed line-clamp-2">{currentLang === 'tr' ? city.description_tr : (city.description_en || city.description_tr)}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{t('wc.stadium')}</p>
                    <p className="text-xs text-amber-400 font-semibold">{city.stadium_slugs?.length || 1}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{t('wc.population')}</p>
                    <p className="text-xs text-white font-semibold">{city.population}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{t('wc.state')}</p>
                    <p className="text-xs text-slate-300 font-medium">{city.state}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{t('wc.timezone')}</p>
                    <p className="text-xs text-slate-300 font-medium flex items-center gap-1"><Clock className="w-3 h-3" />{city.timezone}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
